// pages/legal/PrivacyPage.jsx
// Route: /privacy
import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'July 29, 2026';
const COMPANY        = 'Arhat Info Corp';
const SITE           = 'arhat.info';
const EMAIL          = 'privacy@arhat.info';

const SECTIONS = [
  {
    id: 'overview',
    title: '1. Overview',
    content: `${COMPANY} ("we", "us", "our") operates ${SITE} and related browser extensions. This Privacy Policy explains what personal information we collect, how we use it, and your rights regarding that information.

We are committed to protecting your privacy and complying with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy laws in Canada, as well as the General Data Protection Regulation (GDPR) for users in the European Economic Area.`,
  },
  {
    id: 'what-we-collect',
    title: '2. Information We Collect',
    content: `2.1 Information You Provide
• Account registration: email address, name, password (hashed — never stored in plain text)
• Payment information: processed directly by Stripe — we never see or store your full card number
• Communications: emails you send to us, support requests

2.2 Information Collected Automatically
• Usage data: pages visited, tools used, features clicked, session duration
• Device information: browser type, operating system, screen resolution
• IP address: used for security, fraud prevention, and approximate geolocation (country/province level only)
• Cookies and local storage: see Section 6

2.3 Information NOT Collected
• Financial calculator inputs: processed entirely in your browser — never sent to or stored on our servers
• File tool inputs (image conversion, PDF tools): processed in-browser or on our server temporarily and deleted immediately after processing — never stored
• Chrome extension: we do not collect or store the content of web pages you visit`,
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Information',
    content: `We use your information to:

• Provide, maintain, and improve the Services
• Process payments and manage your subscription
• Send transactional emails (account confirmation, password reset, payment receipts)
• Send service updates and important notices about changes to the Services
• Detect and prevent fraud, abuse, and security incidents
• Comply with legal obligations
• Respond to your support requests

With your explicit consent only:
• Send marketing emails about new features or products (you can opt out at any time)

We do not:
• Sell your personal data to third parties
• Use your data to train AI models
• Share your data with advertisers for targeted advertising`,
  },
  {
    id: 'ai-data',
    title: '4. AI Tool Data',
    content: `When you use our AI-powered tools (Job Search Copilot, Freelancer Toolkit, Developer Tools):

• Your inputs are sent to OpenAI's API for processing
• We do not permanently store your AI inputs or outputs after your session ends
• Session data may be held temporarily in memory during the request and deleted immediately after
• OpenAI processes your inputs under their own privacy policy (openai.com/privacy) and API data usage policy
• OpenAI does not use API inputs to train their models by default (as per their API data usage policy)

We recommend: do not submit highly sensitive personal information (SIN numbers, passwords, confidential business data) into AI tool inputs.`,
  },
  {
    id: 'sharing',
    title: '5. Information Sharing',
    content: `We share your information only in the following circumstances:

5.1 Service Providers (processors)
• Stripe: payment processing (stripe.com/privacy)
• OpenAI: AI tool processing (openai.com/privacy)
• Google Cloud: hosting infrastructure (cloud.google.com/privacy)
• SendGrid/email provider: transactional emails

All service providers are contractually obligated to protect your data and use it only for the purposes we specify.

5.2 Legal Requirements
We may disclose your information if required by law, court order, or government authority, or if we believe disclosure is necessary to protect the rights, property, or safety of ${COMPANY}, our users, or others.

5.3 Business Transfer
If ${COMPANY} is acquired or merges with another company, your information may be transferred as part of that transaction. We will notify you before your information is transferred and becomes subject to a different privacy policy.

5.4 Aggregated/Anonymized Data
We may share aggregated, anonymized data (e.g., "10,000 FIRE calculations were run this month") that cannot identify any individual.`,
  },
  {
    id: 'cookies',
    title: '6. Cookies and Tracking',
    content: `6.1 Essential Cookies (required)
• Authentication tokens: keep you logged in
• CSRF tokens: prevent cross-site request forgery
• Theme preference: remembers your dark/light mode setting

6.2 Analytics (optional, can be declined)
• We may use privacy-friendly analytics (such as Plausible or self-hosted) to understand how tools are used. No personally identifiable information is included in analytics data.

6.3 Advertising Cookies
• If Google AdSense ads are displayed, Google may set cookies for ad personalization. You can opt out at adssettings.google.com.

6.4 Local Storage
• Your currency preference, calculator states, and theme settings are stored in your browser's local storage. This data stays on your device and is not transmitted to us.

You can control cookies through your browser settings. Disabling essential cookies will prevent you from staying logged in.`,
  },
  {
    id: 'retention',
    title: '7. Data Retention',
    content: `• Account data: retained while your account is active and for 30 days after account deletion
• Payment records: retained for 7 years as required by Canadian tax law
• Server logs: retained for 90 days for security and debugging purposes
• AI inputs/outputs: not retained after your session ends
• Anonymized analytics data: retained indefinitely

When you delete your account, we delete your personal data within 30 days, except where retention is required by law or where data has been anonymized.`,
  },
  {
    id: 'rights',
    title: '8. Your Rights',
    content: `8.1 Canadian Users (PIPEDA)
You have the right to:
• Access the personal information we hold about you
• Correct inaccurate information
• Withdraw consent for non-essential data processing
• Request deletion of your personal information (subject to legal retention requirements)
• File a complaint with the Office of the Privacy Commissioner of Canada (priv.gc.ca)

8.2 European Users (GDPR)
In addition to the above, you have the right to:
• Data portability — receive your data in a machine-readable format
• Object to processing based on legitimate interests
• Restrict processing in certain circumstances
• Lodge a complaint with your local supervisory authority

8.3 How to Exercise Your Rights
Email ${EMAIL} with your request. We will respond within 30 days. We may need to verify your identity before processing your request.`,
  },
  {
    id: 'security',
    title: '9. Security',
    content: `We implement industry-standard security measures including:

• HTTPS/TLS encryption for all data in transit
• Passwords hashed using bcrypt (never stored in plain text)
• JWT tokens with short expiry and secure refresh flow
• Cloud SQL PostgreSQL with encrypted storage at rest
• Role-based access controls limiting employee access to user data
• Regular security reviews

No method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.

If you discover a security vulnerability, please report it responsibly to ${EMAIL}.`,
  },
  {
    id: 'children',
    title: '10. Children\'s Privacy',
    content: `The Services are not directed to children under 16 years of age. We do not knowingly collect personal information from children under 16. If you believe we have inadvertently collected information from a child under 16, please contact ${EMAIL} and we will delete it promptly.`,
  },
  {
    id: 'international',
    title: '11. International Transfers',
    content: `${COMPANY} is based in Canada. Your information may be processed in Canada, the United States (by our service providers OpenAI, Stripe, and Google Cloud), and potentially other countries. These countries may have different privacy laws than your country of residence.

When we transfer personal data from the European Economic Area, we rely on appropriate safeguards such as Standard Contractual Clauses. For more information, contact ${EMAIL}.`,
  },
  {
    id: 'changes',
    title: '12. Changes to This Policy',
    content: `We may update this Privacy Policy periodically. When we make material changes, we will notify you by email (to the address associated with your account) and by posting a notice on the Site at least 14 days before the change takes effect.

The effective date at the top of this page indicates when the policy was last updated. Your continued use of the Services after the effective date constitutes acceptance of the updated policy.`,
  },
  {
    id: 'contact',
    title: '13. Contact',
    content: `For privacy-related questions, requests, or complaints:

${COMPANY}
Surrey, British Columbia, Canada

Privacy inquiries: ${EMAIL}
General inquiries: legal@arhat.info

We aim to respond to all privacy requests within 30 days.

If you are in the EU and not satisfied with our response, you have the right to lodge a complaint with your national data protection authority.`,
  },
];

export default function PrivacyPage() {
  return (
    <div style={{
      background: '#07080c', minHeight: '100vh',
      fontFamily: '"DM Sans", system-ui, sans-serif',
      color: '#dce6fa',
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px 80px' }}>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <Link to="/" style={{ fontSize: 13, color: 'rgba(220,230,250,.4)', textDecoration: 'none' }}>
            ← Tylented
          </Link>
          <h1 style={{
            fontSize: 'clamp(1.8rem,4vw,2.8rem)', fontWeight: 900,
            letterSpacing: '-.03em', margin: '16px 0 8px',
          }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(220,230,250,.45)' }}>
            Effective date: <strong style={{ color: 'rgba(220,230,250,.7)' }}>{EFFECTIVE_DATE}</strong>
          </p>
          <p style={{ fontSize: 14, color: 'rgba(220,230,250,.5)', lineHeight: 1.7, marginTop: 12, maxWidth: 680 }}>
            We respect your privacy. This policy explains what data we collect, why, and how you can control it.
          </p>
        </div>

        {/* Table of contents */}
        <div style={{
          padding: '20px 24px', borderRadius: 12,
          background: 'rgba(255,255,255,.03)',
          border: '1px solid rgba(255,255,255,.07)',
          marginBottom: 40,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(220,230,250,.35)', marginBottom: 14 }}>
            Contents
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '6px 24px' }}>
            {SECTIONS.map(s => (
              <a key={s.id} href={`#${s.id}`}
                style={{ fontSize: 13, color: 'rgba(220,230,250,.55)', textDecoration: 'none', lineHeight: 1.8 }}
                onMouseEnter={e => e.target.style.color = '#00c8f0'}
                onMouseLeave={e => e.target.style.color = 'rgba(220,230,250,.55)'}
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div>
          {SECTIONS.map(section => (
            <div key={section.id} id={section.id}
              style={{ borderTop: '1px solid rgba(255,255,255,.06)', padding: '28px 0' }}>
              <h2 style={{ fontSize: 17, fontWeight: 800, color: '#dce6fa', letterSpacing: '-.01em', marginBottom: 14 }}>
                {section.title}
              </h2>
              <div style={{ fontSize: 14, color: 'rgba(220,230,250,.65)', lineHeight: 1.85, whiteSpace: 'pre-line' }}>
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 48, padding: '20px 24px', borderRadius: 12,
          background: 'rgba(0,200,240,.05)', border: '1px solid rgba(0,200,240,.15)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#00c8f0', marginBottom: 8 }}>
            Privacy questions?
          </div>
          <div style={{ fontSize: 13, color: 'rgba(220,230,250,.55)', lineHeight: 1.7 }}>
            Email <a href={`mailto:${EMAIL}`} style={{ color: '#00c8f0', textDecoration: 'none' }}>{EMAIL}</a>{' '}
            and we'll respond within 30 days.
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Link to="/terms"   style={{ fontSize: 13, color: 'rgba(220,230,250,.4)', textDecoration: 'none' }}>Terms and Conditions →</Link>
            <Link to="/pricing" style={{ fontSize: 13, color: 'rgba(220,230,250,.4)', textDecoration: 'none' }}>Pricing →</Link>
            <Link to="/"        style={{ fontSize: 13, color: 'rgba(220,230,250,.4)', textDecoration: 'none' }}>Home →</Link>
          </div>
        </div>

      </div>
    </div>
  );
}
